import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { Save, Preview, ArrowBack } from "@mui/icons-material";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { getDb, callFunction } from "../firebase";
import { UserContext } from "./AuthGuard";
import DOMPurify from "dompurify";

const PostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useContext(UserContext);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const db = getDb();

  const isEditing = Boolean(id);

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);
      const postDoc = await getDoc(doc(db, "posts", id));
      
      if (postDoc.exists()) {
        const postData = postDoc.data();
        
        // Check if user can edit this post
        if (postData.authorId !== user?.id && user?.role !== "admin") {
          navigate("/posts");
          return;
        }
        
        setTitle(postData.title);
        setContent(postData.content);
      } else {
        navigate("/posts");
      }
    } catch (error) {
      console.error("Error loading post:", error);
      navigate("/posts");
    } finally {
      setLoading(false);
    }
  }, [id, db, user, navigate]);

  useEffect(() => {
    if (isEditing) {
      loadPost();
    }
  }, [isEditing, loadPost]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      return;
    }

    try {
      setSaving(true);
      
      if (isEditing) {
        await callFunction(`posts/${id}`, { title, content }, "PUT");
      } else {
        await callFunction("posts", { title, content }, "POST");
      }
      
      navigate("/posts");
    } catch (error) {
      console.error("Error saving post:", error);
    } finally {
      setSaving(false);
    }
  };

  const renderMarkdown = (content) => {
    // Simple markdown-like rendering for demo purposes
    const html = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');
    
    return { __html: DOMPurify.sanitize(html) };
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading post...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          {isEditing ? "Edit Post" : "New Post"}
        </Typography>
        <Button
          component={RouterLink}
          to="/posts"
          startIcon={<ArrowBack />}
          variant="outlined"
        >
          Back to Posts
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={previewMode ? 6 : 12}>
          <Paper sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Content"
              multiline
              rows={15}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              margin="normal"
              required
              helperText="Supports basic markdown: **bold**, *italic*, `code`"
            />

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={!title.trim() || !content.trim() || saving}
              >
                {saving ? <CircularProgress size={20} /> : (isEditing ? "Update" : "Publish")}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Preview />}
                onClick={() => setPreviewMode(!previewMode)}
              >
                {previewMode ? "Hide Preview" : "Show Preview"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {previewMode && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Preview
                </Typography>
                
                <Typography variant="h5" component="h2" gutterBottom>
                  {title || "Untitled Post"}
                </Typography>
                
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                  By {user?.name || user?.email} • {new Date().toLocaleDateString()}
                </Typography>
                
                <Typography
                  variant="body1"
                  dangerouslySetInnerHTML={renderMarkdown(content || "No content yet...")}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PostEditor; 